import * as Icons from '@icons/index'
import { defineComponent } from 'vue'

/**
 *
 * Icon component
 * @component CAIconComponent
 *
 * @prop {string} [dataTest=''] - The data-test attribute for testing purposes
 * @prop {string} icon - The name of the icon to render
 * @prop {string} [id=''] - The ID of the icon element
 * @prop {string} tooltip - The tooltip text to display when hovering over the icon
 *
 * @emit click - Emits a click event when the icon is clicked
 */
export default defineComponent({
	name: 'CAIconComponent',

	props: {
		/** Data test attribute for testing purposes
		 * @default ''
		 * @type {string}
		*/
		dataTest: { default: '', type: String },
		/**
		 * The name of the icon to render
		 * @required
		 * @type {string}
		 */
		icon: { required: true, type: String },
		/**
		 * The ID of the icon element
		 * @default ''
		 * @type {string}
		 */
		id: { default: '', type: String },
		/**
		 * The tooltip text to display when hovering over the icon
		 * @default ''
		 * @type {string}
		 */
		tooltip: { default: '', type: String },
	},

	computed: {
		icons(): string[] {
			return Object.keys(Icons).filter(name => name.startsWith('SVG'))
		},
	},

	emits: ['click'],

	render() {
		const IconComponent = (Icons as Record<string, any>)[this.icon]

		const NamedIconComponent = defineComponent({
			name: `CAIconComponent_${this.icon}`,
			props: {
				/**
				 *
				 * Data test attribute for testing purposes
				 * @default ''
				 * @type {string}
				 */
				dataTest: { default: '', type: String },
				/**
				 *
				 * The name of the icon to render
				 * @required
				 * @type {string}
				 */
				icon: { required: true, type: String },
				/**
				 * The ID of the icon element
				 * @default ''
				 * @type {string}
				 */
				id: { default: '', type: String },
				/**
				 * The tooltip text to display when hovering over the icon
				 * @default ''
				 * @type {string}
				 */
				tooltip: { default: '', type: String },
			},
			render() {
				return <IconComponent
					aria-hidden="true"
					class={`svg-component ${this.icon}`}
					data-test={this.dataTest}
					data-test-component="design-system-icon-component"
					id={this.id}
					v-tooltip={this.tooltip}
					onClick={() => this.$emit('click')}
				/>
			},
		})

		return <NamedIconComponent
			aria-hidden="true"
			dataTest={this.dataTest}
			icon={this.icon}
			id={this.id}
		/>
	},
})
